import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { round } from 'lodash-es';
import { useContext } from '../context';

const COLUMNS = 9;

const calculateUnits = (product, type) =>
  product.density * (product[type] / 100) * (product.percentage / 100);

export function Summary() {
  const { getParam } = useContext();
  const mode = getParam('starterFertilizer.mode');
  const targetRate = getParam('starterFertilizer.targetRate');
  const configuredProducts = getParam('starterFertilizer.products');

  const products = (configuredProducts || [])
    .filter(Boolean)
    .map((product) => ({
      ...product,
      nitrogenUnits: calculateUnits(product, 'nitrogen'),
      phosphorusUnits: calculateUnits(product, 'phosphorus'),
      potassiumUnits: calculateUnits(product, 'potassium'),
      sulfurUnits: calculateUnits(product, 'sulfur'),
    }));

  return (
    <TableContainer>
      <Table
        sx={{
          '& .MuiTableCell-head': {
            backgroundColor: 'transparent',
            fontWeight: 'bold',
          },
          width: '100%',
        }}
      >
        {buildTableSection(...getSummaryTopRows(COLUMNS))}
        {buildTableSection(...getSummaryBottomRows(COLUMNS))}
      </Table>
    </TableContainer>
  );

  function getSummaryTopRows() {
    const headers = [
      'Products',
      'N',
      'P',
      'K',
      'S',
      'Lbs/Gal',
      'Applied Gal',
      'Gal/1k',
      'Percent',
    ];

    const values = products
      .map((product) => [
        product.name,
        round(product.nitrogenUnits * getTargetRate(), 2),
        round(product.phosphorusUnits * getTargetRate(), 2),
        round(product.potassiumUnits * getTargetRate(), 2),
        round(product.sulfurUnits * getTargetRate(), 2),
        product.density,
        round(getTargetRate() * (product.percentage / 100), 2),
        round(product.percentage * 10),
        product.percentage,
      ])
      .concat([emptyRow(COLUMNS)]);

    return [headers, values];
  }

  function emptyRow(columns) {
    return Array(columns).fill('​');
  }

  function getTargetRate() {
    return mode === 'normalized' ? targetRate : 1;
  }

  function getSummaryBottomRows() {
    const sumUnits = (type) =>
      products
        .filter(Boolean)
        .reduce((memo, product) => memo + product[`${type}Units`], 0);

    const nitrogenUnits = sumUnits('nitrogen');
    const phosphorusUnits = sumUnits('phosphorus');
    const potassiumUnits = sumUnits('potassium');
    const sulfurUnits = sumUnits('sulfur');

    const headers = ['Application Units', ...emptyRow(COLUMNS - 1)];
    const values = [
      [
        'Total Units/Gal',
        round(nitrogenUnits, 2),
        round(phosphorusUnits, 2),
        round(potassiumUnits, 2),
        round(sulfurUnits, 2),
        ...emptyRow(COLUMNS - 5),
      ],
      mode === 'full'
        ? [
            'Total Application Units',
            {
              value: '*Will be calculated when Rx is ran',
              colSpan: COLUMNS - 1,
            },
          ]
        : [
            'Total Application Units',
            round(nitrogenUnits * getTargetRate(), 2),
            round(phosphorusUnits * getTargetRate(), 2),
            round(potassiumUnits * getTargetRate(), 2),
            round(sulfurUnits * getTargetRate(), 2),
            ...emptyRow(COLUMNS - 5),
          ],
    ];

    return [headers, values];
  }
}

function buildTableSection(headers, values) {
  return (
    <>
      <TableHead>
        <TableRow>
          {headers.map((header, i) => (
            <TableCell key={i}>{header}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => {
              if (typeof cell === 'object') {
                const { value, ...rest } = cell;

                return (
                  <TableCell key={j} {...rest}>
                    {value}
                  </TableCell>
                );
              }

              return <TableCell key={j}>{cell}</TableCell>;
            })}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}
